import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import { Box, Grid, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  createCreditLineExpress,
  getCreditLineByProduct,
} from 'lib/api/creditLine';
import { CreditLineByProduct } from 'lib/api/creditLine/types';
import CustomButton from 'lib/designSystem/Button';
import TableCard from 'lib/designSystem/Card/TableCard';
import CentralPieChart from 'lib/designSystem/Charts/CentralPieChart';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import SubtitleWithTooltip from 'lib/designSystem/SubtitleWithTooltip';
import { BASE_COLORS, CHART_COLORS } from 'lib/functions/const/colors';
import { formatMoney } from 'lib/functions/utils';
import { formatDate } from 'lib/functions/utils';
import { SUBPRODUCT_LABELS, SubProductId } from 'lib/types/models';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'services/redux/store';

type CreditLineStatusStyle = {
  bg: string;
  color: string;
  label: string;
  icon: typeof CheckCircleIcon;
};

const getCreditLineStyle = (status: string): CreditLineStatusStyle => {
  const style = {
    ACTIVE: {
      bg: CHART_COLORS.green.replace('0.7', '0.1'),
      color: BASE_COLORS.green,
      label: 'Activa',
      icon: CheckCircleIcon,
    },
    IN_REVIEW: {
      bg: CHART_COLORS.yellow.replace('0.7', '0.1'),
      color: BASE_COLORS.yellow,
      label: 'En revisión',
      icon: AssignmentIcon,
    },
    VALIDATING: {
      bg: CHART_COLORS.blue.replace('0.7', '0.1'),
      color: BASE_COLORS.blue,
      label: 'En validación',
      icon: AccessTimeIcon,
    },
    FROZEN: {
      bg: CHART_COLORS.gray.replace('0.7', '0.1'),
      color: BASE_COLORS.gray,
      label: 'Congelada',
      icon: PauseCircleOutlineIcon,
    },
    REJECTED: {
      bg: CHART_COLORS.red.replace('0.7', '0.1'),
      color: BASE_COLORS.red,
      label: 'Rechazada',
      icon: CancelIcon,
    },
  };
  return style[status as keyof typeof style] || style.ACTIVE;
};

const getCreditLineValidity = (data: CreditLineByProduct) => {
  const validUntil = data.validUntil ? new Date(data.validUntil) : null;
  const daysToExpire = validUntil
    ? Math.ceil((validUntil.getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    : null;

  if (data.status !== 'ACTIVE') {
    if (data.status === 'REJECTED') {
      return { text: 'Solicitud rechazada', color: 'text.secondary' as const };
    }
    if (data.status === 'FROZEN') {
      return { text: 'Línea congelada', color: 'text.secondary' as const };
    }
    return { text: 'En proceso', color: 'text.secondary' as const };
  }

  if (!validUntil) {
    return { text: 'Sin vencimiento', color: 'text.secondary' as const };
  }
  const formatted = formatDate(validUntil);

  if (daysToExpire !== null && daysToExpire < 0) {
    return { text: `Venció el ${formatted}`, color: 'error.main' as const };
  }
  if (daysToExpire !== null && daysToExpire <= 30) {
    return {
      text: `Vence en ${daysToExpire} día${daysToExpire === 1 ? '' : 's'} (${formatted})`,
      color: 'warning.main' as const,
    };
  }
  return {
    text: `Vigente hasta ${formatted}`,
    color: 'text.secondary' as const,
  };
};

const getEffectiveCreditLineStyle = (data: CreditLineByProduct) => {
  const style = getCreditLineStyle(data.status);
  const validUntil = data.validUntil ? new Date(data.validUntil) : null;
  const daysToExpire = validUntil
    ? Math.ceil((validUntil.getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    : null;
  const isExpiredWhileActive =
    data.status === 'ACTIVE' && daysToExpire !== null && daysToExpire < 0;

  if (!isExpiredWhileActive) return style;
  return {
    bg: CHART_COLORS.red.replace('0.7', '0.1'),
    color: BASE_COLORS.red,
    label: 'Vencida',
    icon: CancelIcon,
  };
};

const CreditLineState: React.FC<{
  productId: string;
  data?: CreditLineByProduct;
}> = ({ productId, data }) => {
  const dispatch = useDispatch();
  const { mutate, isPending: isCreating } = createCreditLineExpress(dispatch);
  const auth = useSelector((state: RootState) => state.auth);
  const businessIdentifier = auth.user?.businessIdentifier;
  const config = useSelector((state: RootState) => state.clientConfiguration);
  const primaryColor =
    config.configs.pruffConnect_styles_mainColor || '#0F23DF';
  const secondaryColor =
    config.configs.pruffConnect_styles_secondaryColor || primaryColor;

  const hasCreditLine = !!data?.status;
  const effectiveStyle = data ? getEffectiveCreditLineStyle(data) : null;
  const Icon = effectiveStyle?.icon ?? AssignmentIcon;

  const title = (() => {
    if (!hasCreditLine) return 'Solicita tu línea de crédito';
    if (data?.status === 'REJECTED') return 'Tu solicitud fue rechazada';
    if (data?.status === 'FROZEN') return 'Tu línea está congelada';
    if (data?.status === 'VALIDATING')
      return 'Estamos validando tu información';
    return 'Estamos revisando tu solicitud';
  })();

  const description = (() => {
    if (!hasCreditLine) {
      return 'Aún no tienes una línea activa. Al solicitarla, iniciaremos la evaluación y, cuando esté aprobada, aquí verás tus cupos y el cotizador.';
    }
    if (data?.status === 'FROZEN') {
      return 'Tu línea fue congelada temporalmente. Si necesitas ayuda, contáctanos para revisar el estado.';
    }
    if (data?.status === 'REJECTED') {
      return 'Tu solicitud fue rechazada. Si crees que hubo un error, revisa tu información o contáctanos.';
    }
    return 'Cuando esté aprobada, aquí verás tus cupos disponibles y las herramientas asociadas.';
  })();

  const steps = hasCreditLine
    ? [
        {
          title: '1) Validación',
          description:
            'Verificamos información y condiciones para activar la línea.',
        },
        {
          title: '2) Revisión',
          description: 'Evaluamos la solicitud y los antecedentes del negocio.',
        },
        {
          title: '3) Activación',
          description: 'Al aprobarse, verás tus cupos y podrás cotizar.',
        },
      ]
    : [
        {
          title: '1) Solicitud',
          description: 'Completa el proceso para iniciar la evaluación.',
        },
        {
          title: '2) Revisión',
          description: 'Evaluamos tu solicitud y validamos la información.',
        },
        {
          title: '3) Activación',
          description: 'Cuando esté aprobada, podrás ver cupos y cotizar.',
        },
      ];

  const handleCreateCreditLine = () => {
    if (!businessIdentifier) return;
    void mutate({ businessIdentifier, productType: productId });
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight="60vh"
      px={2}
      py={2}
      gap={2}
    >
      <TableCard
        enabledEffects={false}
        sx={{
          width: '100%',
          maxWidth: 820,
          border: `1px solid ${alpha(primaryColor, 0.08)}`,
        }}
      >
        <Stack direction="column" gap={2}>
          <Stack direction="row" gap={2} alignItems="flex-start">
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: hasCreditLine
                  ? effectiveStyle?.bg
                  : alpha(primaryColor, 0.08),
                color: hasCreditLine ? effectiveStyle?.color : primaryColor,
                flex: '0 0 auto',
                mt: 0.5,
              }}
            >
              <Icon sx={{ fontSize: 22 }} />
            </Box>
            <Stack direction="column">
              <Typography variant="h6" color="text.primary" mt={0.5}>
                {title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {description}
              </Typography>
            </Stack>
          </Stack>

          <Grid container spacing={2}>
            {steps.map((step) => (
              <Grid item xs={12} md={4} key={step.title}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: '12px',
                    bgcolor: alpha(secondaryColor, hasCreditLine ? 0.1 : 0.03),
                    border: `1px solid ${alpha(primaryColor, hasCreditLine ? 0.1 : 0.08)}`,
                    height: '100px',
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                    {step.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {step.description}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          {!hasCreditLine && (
            <Box display="flex" gap={1.5} flexWrap="wrap">
              <CustomButton
                color="primary"
                onClick={handleCreateCreditLine}
                disabled={isCreating}
                loading={isCreating}
                size="large"
              >
                Solicitar Línea de Crédito
              </CustomButton>
            </Box>
          )}
        </Stack>
      </TableCard>

      {data && (
        <Box width="100%" maxWidth={820}>
          <CreditLineLegend data={data} />
        </Box>
      )}
    </Box>
  );
};

const CreditLineLegend: React.FC<{ data: CreditLineByProduct }> = ({
  data,
}) => {
  const effectiveChipStyle = getEffectiveCreditLineStyle(data);
  const validity = getCreditLineValidity(data);

  return (
    <Typography
      variant="caption"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        color: 'text.secondary',
      }}
    >
      <Box
        component="span"
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          display: 'inline-block',
          bgcolor: effectiveChipStyle.color,
        }}
      />
      <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
        Línea de crédito:
      </Box>
      <Box
        component="span"
        sx={{ fontWeight: 700, color: effectiveChipStyle.color }}
      >
        {effectiveChipStyle.label}
      </Box>
      <Box component="span" sx={{ color: validity.color }}>
        · {validity.text}
      </Box>
    </Typography>
  );
};

const CreditLineChart: React.FC<{ data: CreditLineByProduct }> = ({ data }) => {
  const config = useSelector(
    (state: RootState) => state.clientConfiguration.configs,
  );
  const mainColor = config.pruffConnect_styles_mainColor;
  const secondaryColor = config.pruffConnect_styles_secondaryColor;
  const chartColors = [mainColor, secondaryColor];

  return (
    <Box pt={0.5}>
      <Grid container spacing={3}>
        {data.subProducts.map((product) => {
          const subProductData = [
            { name: 'Utilizado', value: product.used },
            { name: 'Disponible', value: product.amount - product.used },
          ];

          const tooltipContent = product?.maxDocumentAmount
            ? `Monto maximo por operación: ${formatMoney(product.maxDocumentAmount)}`
            : 'No hay un monto maximo por operación';
          const title =
            SUBPRODUCT_LABELS[product.subProductId as SubProductId] ?? '';

          return (
            <Grid item sm={4} key={product.subProductId}>
              <TableCard>
                <SubtitleWithTooltip
                  title={title}
                  tooltipContent={tooltipContent}
                />
                <CentralPieChart data={subProductData} colors={chartColors} />
              </TableCard>
            </Grid>
          );
        })}
      </Grid>
      <Box width="100%" maxWidth={820} m={2}>
        <CreditLineLegend data={data} />
      </Box>
    </Box>
  );
};

const CreditLineStatus: React.FC<{ productId: string }> = ({ productId }) => {
  const dispatch = useDispatch();
  const auth = useSelector((state: RootState) => state.auth);
  const businessIdentifier = auth.user?.businessIdentifier ?? null;
  const { data, isPending, isFetching } = getCreditLineByProduct(
    dispatch,
    businessIdentifier,
    productId,
  );

  if (isPending || isFetching) return <CenteredCircularProgress />;
  if (!data?.status) return <CreditLineState productId={productId} />;
  if (data.status !== 'ACTIVE' || !data.subProducts?.length) {
    return <CreditLineState productId={productId} data={data} />;
  }
  return <CreditLineChart data={data} />;
};

export default CreditLineStatus;
